import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FiUser, FiMail, FiLock, FiGlobe, FiPhone, FiBox, FiArrowRight } from "react-icons/fi";
import { ApiHandler } from "../../helper/ApiHandler";
import { setUserProfileData, setUserToken } from "../../redux/slices/userSlice";
import illustration from "../../assest/Sign-up-amico.png"
import SEO from "../../utils/Seo";

const SIGNUP_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,500;0,9..144,600;1,9..144,500&family=Manrope:wght@400;500;600;700;800&display=swap');

  .su-display { font-family: 'Fraunces', ui-serif, Georgia, serif; }
  .su-body { font-family: 'Manrope', ui-sans-serif, system-ui, sans-serif; }

  @keyframes su-float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
  .su-float { animation: su-float 4.5s ease-in-out infinite; }

  @media (prefers-reduced-motion: reduce) {
    .su-float { animation: none !important; }
  }

  .scrollbar-hide::-webkit-scrollbar { display: none; }
  .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
`;

const FIELD_ICONS = {
  domain: FiGlobe,
  app_name: FiBox,
  fullName: FiUser,
  email: FiMail,
  cell: FiPhone,
  password: FiLock,
  confirmPassword: FiLock,
};

const Signup = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    domain: "",
    app_name: "",
    fullName: "",
    email: "",
    cell: "",
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const { domain, app_name, fullName, email, cell, password, confirmPassword } = formData;
    const newErrors = {};

    if (!domain.trim()) {
      newErrors.domain = "App URL is required";
    } else if (!/^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/.test(domain.trim())) {
      newErrors.domain = "Enter a valid URL (e.g. example.com)";
    }

    if (!app_name.trim()) {
      newErrors.app_name = "App name is required";
    } else if (app_name.trim().length < 2) {
      newErrors.app_name = "App name is too short";
    }

    if (!fullName.trim()) {
      newErrors.fullName = "Full name is required";
    } else if (!/^[a-zA-Z\s.'-]{2,}$/.test(fullName.trim())) {
      newErrors.fullName = "Enter a valid name";
    }

    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      newErrors.email = "Enter a valid email address";
    }

    if (!cell.trim()) {
      newErrors.cell = "Phone number is required";
    } else if (!/^\d{10}$/.test(cell.trim())) {
      newErrors.cell = "Enter a valid 10-digit phone number";
    }

    if (!password) {
      newErrors.password = "Password is required";
    } else if (password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!validate()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    setIsSubmitting(true);

    const { domain, app_name, fullName, email, cell, password, confirmPassword } = formData;

    const body = {
      domain: domain,
      app_name: app_name,
      full_name: fullName,
      email: email,
      cell: cell,
      password: password,
      confirm_password: confirmPassword,
    };

    try {
      const result = await ApiHandler("/register.php", "POST", body, undefined, dispatch, navigate);

      if (result.data.status === "1") {
        const getProfile = await ApiHandler("/get_profile.php", "GET", undefined, result.data.data.token, dispatch, navigate);
        if (getProfile.data.status === "1") {
          toast.success(result.data.msg);
          dispatch(setUserToken(result.data.data.token));
          dispatch(setUserProfileData(getProfile.data.data[0]));
          navigate("/dashboard");
        }
      } else {
        toast.error(result.data.msg);
        setFormData({
          ...formData,
          domain: "",
          app_name: "",
          fullName: "",
          email: "",
          cell: "",
          password: "",
          confirmPassword: "",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const fields = [
    { label: "App Url", name: "domain", type: "text", placeholder: "example.com" },
    { label: "App Name", name: "app_name", type: "text", placeholder: "My Awesome App" },
    { label: "Full Name", name: "fullName", type: "text", placeholder: "John Doe" },
    { label: "Email", name: "email", type: "email", placeholder: "you@example.com" },
    { label: "Phone Number / Cell", name: "cell", type: "text", placeholder: "9876543210" },
    { label: "Password", name: "password", type: "password", placeholder: "At least 6 characters" },
    { label: "Confirm Password", name: "confirmPassword", type: "password", placeholder: "Re-enter your password" },
  ];

  return (
    <>
      <SEO
        title="Sign Up - Join MyAd360"
        description="Create your free MyAd360 account and start boosting your website ad revenue with Google AdX & header bidding."
      />
      <style>{SIGNUP_STYLES}</style>

      <div
        className="su-body min-h-screen flex items-center justify-center px-3 sm:px-4 py-4 sm:py-8"
        style={{
          background:
            "radial-gradient(90% 70% at 15% 0%, #FFF4E5 0%, transparent 55%), radial-gradient(80% 60% at 100% 100%, #FEF3E5 0%, transparent 55%), #FFFBF5",
        }}
      >
        <div
  className="max-w-6xl w-full h-[640px] max-h-[92vh] bg-white shadow-[0_30px_80px_-25px_rgba(28,20,16,0.25)] overflow-hidden grid grid-cols-1 md:grid-cols-2 border border-[#F0DCC8]"
  style={{ borderRadius: "1.5rem" }}
>
          {/* Left: Illustration */}
  {/* Left: Illustration */}
<div
  className="hidden md:flex h-full items-center justify-center relative overflow-hidden"
  style={{
    background:
      "radial-gradient(120% 100% at 30% 20%, #FEF3E5 0%, #FFF4E5 60%, #FFFBF5 100%)",
  }}
>
  <div
    aria-hidden="true"
    className="absolute -top-10 -left-10 w-56 h-56 rounded-full opacity-30 blur-3xl"
    style={{ background: "radial-gradient(circle, #F59E0B, transparent 70%)" }}
  />
  <div
    aria-hidden="true"
    className="absolute -bottom-14 -right-10 w-56 h-56 rounded-full opacity-25 blur-3xl"
    style={{ background: "radial-gradient(circle, #D97706, transparent 70%)" }}
  />
  <img
    src={illustration}
    alt="Signup Illustration"
    className="su-float max-h-[450px] w-auto object-contain relative z-10"
  />
</div>

          {/* Right: Form */}
  <div className="h-full p-5 xs:p-6 sm:p-10 overflow-y-auto scrollbar-hide">

            <h2
              className="text-2xl sm:text-3xl font-bold text-center text-[#1C1410]"
              style={{ fontFamily: "'Fraunces', ui-serif, Georgia, serif" }}
            >
              Create Your{" "}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#D97706] via-[#E8882D] to-[#EA580C]">
                Account
              </span>
            </h2>
            <p className="text-center text-[#8A7560] mb-6 text-sm">Sign up to get started</p>

            <form onSubmit={handleSubmit} noValidate>
              {fields.map(({ label, name, type, placeholder }) => {
                const Icon = FIELD_ICONS[name];
                return (
                  <div key={name} className="mb-4">
                    <label htmlFor={name} className="block mb-1 text-sm font-medium text-[#3D2E22]">
                      {label}
                    </label>
                    <div className="relative">
                      <Icon className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A08E7A]" />
                      <input
                        type={type}
                        id={name}
                        name={name}
                        value={formData[name]}
                        onChange={handleChange}
                        placeholder={placeholder}
                        className={`w-full h-12 pl-11 pr-4 bg-white border rounded-xl text-sm focus:outline-none focus:ring-2 transition-all ${
                          errors[name]
                            ? "border-red-400 focus:border-red-400 focus:ring-red-100"
                            : "border-[#F0DCC8] focus:border-[#D97706] focus:ring-[#FDE8CD]"
                        }`}
                      />
                    </div>
                    {errors[name] && (
                      <p className="mt-1 text-xs text-red-500">{errors[name]}</p>
                    )}
                  </div>
                );
              })}

              <button
                type="submit"
                disabled={isSubmitting}
                className="group w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#D97706] to-[#B45309] hover:from-[#7C3A0E] hover:to-[#D97706] text-white py-3 rounded-xl text-sm font-bold shadow-[0_16px_34px_-14px_rgba(217,119,6,0.5)] transition-all duration-300 disabled:opacity-70 mt-2"
              >
                {isSubmitting ? "Signing up..." : "Sign Up"}
                {!isSubmitting && (
                  <FiArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                )}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-[#8A7560]">
              Already have an account?{" "}
              <span
                className="text-[#D97706] font-bold cursor-pointer hover:text-[#B45309] hover:underline transition-colors"
                onClick={() => navigate("/login")}
              >
                Log In
              </span>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Signup;